import { createFileRoute, useSearch } from '@tanstack/react-router'
import { useEffect, useState } from 'react'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { canonicalLink } from '@/lib/seo'

interface SearchParams {
  token?: string
  action?: 'cancel' | 'reschedule'
}

export const Route = createFileRoute('/appointment/manage')({
  validateSearch: (search: Record<string, unknown>): SearchParams => ({
    token: typeof search.token === 'string' ? search.token : undefined,
    action:
      search.action === 'cancel' || search.action === 'reschedule'
        ? search.action
        : undefined,
  }),
  head: () => ({
    meta: [
      { title: 'Manage Appointment — BLVDS Detailing' },
      { name: 'robots', content: 'noindex,nofollow' },
    ],
    links: [canonicalLink('/appointment/manage')],
  }),
  component: ManageAppointmentPage,
})

interface AppointmentInfo {
  id: string
  service: string
  scheduledAt: string
  serviceAddress: string
  status: string
}

function ManageAppointmentPage() {
  const { token, action: initialAction } = useSearch({ from: '/appointment/manage' })
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [appointment, setAppointment] = useState<AppointmentInfo | null>(null)
  const [action, setAction] = useState<'cancel' | 'reschedule'>(
    initialAction ?? 'cancel'
  )
  const [newDate, setNewDate] = useState('')
  const [newTime, setNewTime] = useState('')
  const [submitting, setSubmitting] = useState(false)
  const [done, setDone] = useState<null | { status: string; scheduledAt?: string }>(null)

  useEffect(() => {
    if (!token) {
      setError('Missing token')
      setLoading(false)
      return
    }
    fetch(`/api/public/appointment-action?token=${encodeURIComponent(token)}`)
      .then(async (r) => {
        const data = await r.json()
        if (!r.ok) throw new Error(data.error || 'Failed to load appointment')
        setAppointment(data.appointment)
      })
      .catch((e) => setError(e.message))
      .finally(() => setLoading(false))
  }, [token])

  const handleSubmit = async () => {
    if (!token) return
    setSubmitting(true)
    setError(null)
    try {
      const body: any = { token, action }
      if (action === 'reschedule') {
        if (!newDate || !newTime) {
          throw new Error('Please pick a date and time')
        }
        body.newScheduledAt = new Date(`${newDate}T${newTime}`).toISOString()
      }
      const res = await fetch('/api/public/appointment-action', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      })
      const data = await res.json()
      if (!res.ok) throw new Error(data.error || 'Action failed')
      setDone({ status: data.status, scheduledAt: data.scheduledAt })
    } catch (e: any) {
      setError(e.message)
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <div className="min-h-screen bg-background flex items-center justify-center p-6">
      <div className="w-full max-w-lg bg-card border rounded-2xl p-8 shadow-sm">
        <h1 className="text-2xl font-bold mb-2">Manage your booking</h1>
        <p className="text-sm text-muted-foreground mb-6">
          Cancel or reschedule your appointment with BLVDS Detailing.
        </p>

        {loading && <p className="text-sm">Loading…</p>}

        {!loading && error && !appointment && (
          <p className="text-sm text-destructive">{error}</p>
        )}

        {appointment && !done && (
          <>
            <div className="rounded-lg bg-muted/50 p-4 mb-6 text-sm space-y-1">
              <p><span className="text-muted-foreground">Service:</span> {appointment.service}</p>
              <p>
                <span className="text-muted-foreground">When:</span>{' '}
                {new Date(appointment.scheduledAt).toLocaleString('en-US', {
                  weekday: 'long', month: 'short', day: 'numeric', year: 'numeric',
                  hour: 'numeric', minute: '2-digit', timeZone: 'America/New_York',
                })}
              </p>
              <p><span className="text-muted-foreground">Address:</span> {appointment.serviceAddress}</p>
              <p><span className="text-muted-foreground">Status:</span> {appointment.status}</p>
            </div>

            {appointment.status === 'cancelled' ? (
              <p className="text-sm">This booking has already been cancelled.</p>
            ) : (
              <>
                <div className="flex gap-2 mb-6">
                  <Button
                    variant={action === 'reschedule' ? 'default' : 'outline'}
                    onClick={() => setAction('reschedule')}
                    className="flex-1"
                  >
                    Reschedule
                  </Button>
                  <Button
                    variant={action === 'cancel' ? 'default' : 'outline'}
                    onClick={() => setAction('cancel')}
                    className="flex-1"
                  >
                    Cancel
                  </Button>
                </div>

                {action === 'reschedule' && (
                  <div className="space-y-3 mb-6">
                    <div>
                      <Label htmlFor="newDate">New date</Label>
                      <Input
                        id="newDate"
                        type="date"
                        value={newDate}
                        min={new Date().toISOString().slice(0, 10)}
                        onChange={(e) => setNewDate(e.target.value)}
                      />
                    </div>
                    <div>
                      <Label htmlFor="newTime">New time</Label>
                      <Input
                        id="newTime"
                        type="time"
                        value={newTime}
                        onChange={(e) => setNewTime(e.target.value)}
                      />
                    </div>
                  </div>
                )}

                {error && <p className="text-sm text-destructive mb-4">{error}</p>}

                <Button
                  onClick={handleSubmit}
                  disabled={submitting}
                  className="w-full"
                  variant={action === 'cancel' ? 'destructive' : 'default'}
                >
                  {submitting
                    ? 'Working…'
                    : action === 'cancel'
                      ? 'Confirm cancellation'
                      : 'Confirm new time'}
                </Button>
              </>
            )}
          </>
        )}

        {done && (
          <div className="text-sm space-y-2">
            {done.status === 'cancelled' && (
              <p>Your booking has been cancelled. We've notified the team.</p>
            )}
            {done.status === 'rescheduled' && done.scheduledAt && (
              <p>
                Your booking has been rescheduled to{' '}
                <strong>
                  {new Date(done.scheduledAt).toLocaleString('en-US', {
                    weekday: 'long', month: 'short', day: 'numeric', year: 'numeric',
                    hour: 'numeric', minute: '2-digit', timeZone: 'America/New_York',
                  })}
                </strong>
                .
              </p>
            )}
            <p className="text-muted-foreground">You can close this page.</p>
          </div>
        )}
      </div>
    </div>
  )
}
